import React from "react";

import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useParams } from "react-router-dom-v5-compat";

import {
  LinkButton,
  PrimaryAction,
  RecordFeatureView,
  TitleBar,
} from "./Atoms";
import { ItemBox, ItemCountdown } from "./Sections/Section";
import { useVenue } from "./VenueContext";
import { getPromotions } from "./menuSelectors";
import { useFetchLiveItemCountdown } from "./queries";
import type { Item, Menu } from "./schema";
import { getVenueHasMobileOrdering } from "./venueSelectors";

const DefaultSection = ({ menu }: { menu: Menu }) => {
  const { t } = useTranslation();
  const orderingEnabled = getVenueHasMobileOrdering(useVenue());
  const firstSection = menu.sections[0];
  return (
    <>
      <div className="space-y-24 ">
        <h2 className="text-xl md:text-4xl font-bold md:text-center">
          {firstSection.name}
        </h2>

        <div className="grid grid-cols-1 gap-y-12 gap-x-48 xl:grid-cols-2 ">
          {firstSection.items.map((item, index) => (
            <Link
              className="rounded-venue-brand"
              key={index}
              to={`/${firstSection.uuid}/${item.uuid}/new`}
            >
              <ItemBox item={item} key={`${index}-${item.uuid}`} />
            </Link>
          ))}
        </div>
      </div>
      <PrimaryAction>
        <LinkButton primary to="your-order/pending">
          {orderingEnabled
            ? t("mainPage.yourOrder")
            : t("mainPage.yourWishlist")}
        </LinkButton>
      </PrimaryAction>
    </>
  );
};

const FavouriteItem = ({ item }: { item: Item }) => {
  const { t } = useTranslation();
  const itemCountdownQuery = useFetchLiveItemCountdown(item.uuid);
  const soldOut =
    itemCountdownQuery.data && itemCountdownQuery.data.remainingCount <= 0;
  return (
    <Link
      to={`promotions/${item.uuid}`}
      className={classNames(
        "rounded-venue-brand bg-venue-tertiary shadow-venue-brand-style relative",
      )}
    >
      <div className="aspect-w-7 aspect-h-10 ">
        {item.heroImageUrl && (
          <img
            className={classNames("rounded-t-venue-brand object-cover ", {
              "opacity-50 grayscale": soldOut,
            })}
            src={item.heroImageUrl}
          />
        )}
      </div>
      <div className="absolute top-0 right-0 p-8">
        <ItemCountdown count={itemCountdownQuery.data?.remainingCount} />
      </div>
      <div className="p-12 space-y-4">
        <div className="text-xl flex justify-between font-lg">
          <div>{item.name}</div>
          <div>{item.salesPrice}</div>
        </div>
        <div className="text-xs">{item.marketingBlurb}</div>
      </div>
    </Link>
  );
};

const Promotions = ({ menu }: { menu: Menu }) => {
  const { t } = useTranslation();
  const orderingEnabled = getVenueHasMobileOrdering(useVenue());
  const promotions = getPromotions(menu);

  if (promotions.length == 0) {
    return <DefaultSection menu={menu} />;
  }
  return (
    <>
      <div className="space-y-24 ">
        <h2 className="text-xl md:text-4xl font-bold md:text-center">
          {t("promotions.title")}
        </h2>

        {false ? (
          <div className="grid grid-cols-1 gap-y-12 gap-x-48 xl:grid-cols-2 ">
            {promotions.map((promotedItem, index) => (
              <Link
                className="rounded-venue-brand"
                key={index}
                to={`promotions/${promotedItem.uuid}`}
              >
                <ItemBox
                  item={{
                    ...promotedItem,
                    detail: promotedItem.marketingBlurb,
                  }}
                />
              </Link>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-12 sm:gap-x-24 gap-y-32 ">
            {promotions.map((promotedItem, index) => (
              <FavouriteItem item={promotedItem} key={promotedItem.uuid} />
            ))}
          </div>
        )}
      </div>
      <PrimaryAction>
        <LinkButton primary to="your-order/pending">
          {orderingEnabled
            ? t("mainPage.yourOrder")
            : t("mainPage.yourWishlist")}
        </LinkButton>
      </PrimaryAction>
    </>
  );
};

export const Promotion = ({ menu }: { menu: Menu }) => {
  const { itemUuid } = useParams();
  const promotedItem = menu.sections
    .flatMap((i) => i.items)
    .find((p) => p.uuid == itemUuid);
  if (!promotedItem) {
    return <div>No Promotion </div>;
  }
  return (
    <RecordFeatureView featureName="Online / Promotion">
      <div>
        <div className="hidden md:block">
          <TitleBar>Promotions</TitleBar>
        </div>
        <Outlet />
      </div>
    </RecordFeatureView>
  );
};

export default Promotions;
